/** Shared types for personal performance dashboard tabs. */

export type TabId = 'business' | 'portfolio' | string;

export interface CustomTab {
  id: string;
  name: string;
  kpiIds: string[];
}

export interface Scope {
  businessName: string | null;
  functionalName: string | null;
  geographicName: string | null;
}

export interface HistoryPoint {
  periodTo: string | null;
  value: number;
}

export type CompareHorizon = 'STRATEGIC' | 'ANNUAL' | 'QUARTERLY';

export interface ComparePrefs {
  horizon: CompareHorizon;
  year: number;
  /** 1–4 when horizon is QUARTERLY; kept so switching back lands on the same quarter. */
  quarter: number;
}

/** Periods nested under each plan type — from that plan at this tenant + hierarchy. */
export interface CompareOptions {
  strategicYears: number[];
  annualYears: number[];
  quarterlyYear: number;
  quarters: number[];
}

export function currentCalendarYear(): number {
  return new Date().getFullYear();
}

export function currentCalendarQuarter(): number {
  return Math.floor(new Date().getMonth() / 3) + 1;
}

/** Last finished quarter. The in-progress quarter often has a plan but no actual yet. */
export function lastCompletedQuarter(from = new Date()): { year: number; quarter: number } {
  const month = from.getMonth();
  if (month < 3) return { year: from.getFullYear() - 1, quarter: 4 };
  return { year: from.getFullYear(), quarter: Math.floor(month / 3) };
}

export function defaultComparePrefs(): ComparePrefs {
  return {
    horizon: 'ANNUAL',
    year: currentCalendarYear(),
    quarter: lastCompletedQuarter().quarter
  };
}

export type PortfolioViewMode = 'year' | 'quarter';

export interface PortfolioView {
  mode: PortfolioViewMode;
  year: number;
  quarter: number;
}

export function defaultPortfolioView(): PortfolioView {
  return {
    mode: 'year',
    year: currentCalendarYear(),
    quarter: currentCalendarQuarter()
  };
}

export function emptyCompareOptions(): CompareOptions {
  const year = currentCalendarYear();
  return {
    strategicYears: [year],
    annualYears: [year],
    quarterlyYear: year,
    quarters: [1, 2, 3, 4]
  };
}

export const KPI_ROW_COLUMNS = [2, 3, 4, 5, 6] as const;
export type KpiRowColumns = (typeof KPI_ROW_COLUMNS)[number];
export const DEFAULT_KPI_COLUMNS: KpiRowColumns = 4;

/** Guidance only — extra tabs, not a hard cap. */
export const GUIDED_TAB_KPI_MAX = 8;

export const TILE_SORTS = ['default', 'name', 'unfavorable'] as const;
export type TileSort = (typeof TILE_SORTS)[number];
export const DEFAULT_TILE_SORT: TileSort = 'name';

/** Complete class names so Tailwind keeps every column count. */
const KPI_GRID_CLASS: Record<KpiRowColumns, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6'
};

export function kpiGridClass(columns: number): string {
  if ((KPI_ROW_COLUMNS as readonly number[]).includes(columns)) {
    return KPI_GRID_CLASS[columns as KpiRowColumns];
  }
  return KPI_GRID_CLASS[DEFAULT_KPI_COLUMNS];
}

export interface KpiTile {
  kpiId: string;
  kpiName: string;
  tags?: string[];
  type?: string | null;
  unit: string | null;
  direction: 'MAXIMUM' | 'MINIMUM';
  actual: number | null;
  strategicTarget: number | null;
  annualTarget: number | null;
  quarterlyTarget: number | null;
  primaryTarget: number | null;
  compareHorizon?: CompareHorizon;
  progressPct: number | null;
  /** Actual vs the selected plan target. Higher is better for every direction. */
  pace?: number | null;
  color: 'GREEN' | 'YELLOW' | 'RED' | null;
  history: HistoryPoint[];
  redacted?: boolean;
}

export interface PortfolioMetrics {
  inchstonesDue: number;
  inchstonesAchieved: number;
  inchstonesDelayed: number;
  risksAdded: number;
  risksClosed: number;
  risksOutstanding: number;
  issuesAdded: number;
  issuesClosed: number;
  issuesOutstanding: number;
  actionsAdded: number;
  actionsClosed: number;
  actionsOutstanding: number;
  projectsCreated: number;
  projectsClosed: number;
  projectsOpen: number;
  peopleOnProjects: number;
  spendTarget: number;
  spendActual: number;
  benefitsTarget: number;
  benefitsActual: number;
}

export interface CatalogKpi {
  kpiId: string;
  kpiName: string;
  tags?: string[];
  type?: string | null;
  unit?: string | null;
}

export const COMPOSITE_KINDS = [
  'donut',
  'stack-column',
  'stack-row',
  'cluster-column',
  'cluster-row'
] as const;

export type CompositeKind = (typeof COMPOSITE_KINDS)[number];

export interface CompositeDef {
  id: string;
  tab: string;
  name: string;
  kind: CompositeKind;
  memberKpiIds: string[];
  denominatorKpiId: string | null;
  hidden?: boolean;
}

export interface CompositeSlice {
  kpiId: string;
  kpiName: string;
  plan: number | null;
  actual: number | null;
  planPct: number | null;
  actualPct: number | null;
}

export interface CompositeCard {
  id: string;
  tab: string;
  name: string;
  kind: CompositeKind;
  type: string | null;
  unit: string | null;
  mixError?: string;
  inferredTotal: boolean;
  identityBroken: boolean;
  memberKpiIds?: string[];
  denominatorKpiId?: string | null;
  denominatorName: string | null;
  hidden?: boolean;
  slices: CompositeSlice[];
  color?: 'GREEN' | 'YELLOW' | 'RED' | null;
}

/** Non-blue mix palette: contrasting, not neon. */
export const COMPOSITE_SLICE_COLORS = [
  '#C45C26',
  '#7A4E8A',
  '#C4A035',
  '#3D7A6A',
  '#B85C5C',
  '#5C6B4A',
  '#D97706',
  '#6B5B4F'
];

export const COMPOSITE_REMAINDER_COLOR = '#9A8B7A';
export const COMPOSITE_PLAN_BAR = '#C4A035';
export const COMPOSITE_ACTUAL_BAR = '#C45C26';

export interface TabPayload {
  tab: TabId;
  needsHierarchy?: boolean;
  message?: string;
  scope: Scope | null;
  tiles: KpiTile[];
  catalog?: CatalogKpi[];
  customTabs?: CustomTab[];
  composites?: CompositeCard[];
  metrics: PortfolioMetrics | null;
  hiddenIds?: string[];
  selectedIds?: string[];
  compare?: ComparePrefs;
  availableYears?: number[];
  compareOptions?: CompareOptions;
  columns?: number;
  sort?: TileSort;
  portfolioView?: PortfolioView;
  thresholds?: { yellowOffPct: number; redOffPct: number };
}

export const COLOR_BADGE: Record<string, string> = {
  GREEN: 'bg-green-100 text-green-800',
  YELLOW: 'bg-amber-100 text-amber-800',
  RED: 'bg-red-100 text-red-800'
};

export const GAUGE_FILL: Record<string, string> = {
  GREEN: '#15803d',
  YELLOW: '#C4A035',
  RED: '#b91c1c',
  NONE: 'var(--color-primary-text)'
};

export function fmt(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  if (Number.isInteger(value)) return String(value);
  return value.toLocaleString(undefined, { maximumFractionDigits: 1, minimumFractionDigits: 0 });
}

export function isPercentKpi(tile: Pick<KpiTile, 'type' | 'unit'>): boolean {
  const type = String(tile.type || '').toUpperCase();
  const unit = String(tile.unit || '').toLowerCase();
  return type === 'PERCENTAGE' || unit.includes('%') || unit.includes('percent');
}

export function fmtPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${value.toFixed(1)}%`;
}

export function isMoneyKpi(tile: Pick<KpiTile, 'type' | 'unit'>): boolean {
  const type = String(tile.type || '').toUpperCase();
  if (type === 'CURRENCY') return true;
  const unit = String(tile.unit || '').trim().toLowerCase();
  return unit === 'usd' || unit === 'currency' || unit === '$' || unit.includes('usd') || unit.startsWith('$');
}

/** Stored currency is raw units. Display is always millions of dollars. */
export function fmtMoney(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `$${(value / 1_000_000).toFixed(1)}M`;
}

export function fmtKpiValue(
  tile: Pick<KpiTile, 'type' | 'unit'>,
  value: number | null | undefined
): string {
  if (isMoneyKpi(tile)) return fmtMoney(value);
  if (isPercentKpi(tile)) return fmtPercent(value);
  return fmt(value);
}

export function fmtKpiWithUnit(tile: Pick<KpiTile, 'type' | 'unit'>, value: number | null | undefined): string {
  if (tile == null) return '—';
  const text = fmtKpiValue(tile, value);
  if (text === '—' || isMoneyKpi(tile) || isPercentKpi(tile)) return text;
  return tile.unit ? `${text} ${tile.unit}` : text;
}

/**
 * Dials only when the number is already a share: percentage, % unit, or 0–1 scale.
 * Currency, counts, time, and ratios above 1 use comparison bars.
 */
export function usesDial(tile: Pick<KpiTile, 'type' | 'unit' | 'actual' | 'quarterlyTarget' | 'annualTarget' | 'strategicTarget'>): boolean {
  const type = String(tile.type || '').toUpperCase();
  const unit = String(tile.unit || '').toLowerCase();
  if (type === 'PERCENTAGE') return true;
  if (unit.includes('%') || unit.includes('percent')) return true;
  const values = [tile.actual, tile.quarterlyTarget, tile.annualTarget, tile.strategicTarget].filter(
    (value): value is number => typeof value === 'number' && Number.isFinite(value)
  );
  if (values.length === 0) return false;
  return Math.max(...values) <= 1.2 && Math.min(...values) >= 0;
}

export function gaugeFill(color: KpiTile['color']): string {
  if (!color) return GAUGE_FILL.NONE;
  return GAUGE_FILL[color] || GAUGE_FILL.NONE;
}

/** Same progress math as backend progressPercent — used so one dial can show both targets. */
export function progressToward(
  actual: number | null,
  target: number | null,
  direction: KpiTile['direction']
): number | null {
  if (actual === null || target === null || target === 0) return null;
  if (direction === 'MAXIMUM') {
    return Math.max(0, Math.min(100, (actual / target) * 100));
  }
  if (actual <= target) return 100;
  return Math.max(0, Math.min(100, (target / actual) * 100));
}

/** Horizon rings — same colors on every graph. Inner → outer. */
export const QUARTERLY_RING = '#004D99';
export const ANNUAL_RING = '#5B94DD';
export const STRATEGIC_RING = '#95C4FF';
